import { ErrorCode, ConsumerLookupAction, ConsumerLookupCohort, ConsumerLookupRecognizedBy } from '@afterpaytouch/portal-api/types'
import { createSlice, isAnyOf, PayloadAction, Slice } from '@reduxjs/toolkit'
import { v4 as uuid } from 'uuid'
import {
  accountLookup,
  authLogin,
  authLogout,
  authSessionsService,
  check2faCode,
  check2faDob,
  checkPersistentLogin,
  closeCheckout,
  confirmRegistrationMobile,
  createFingerprint,
  emailLookup,
  finaliseAccountDetails,
  getCheckout,
  loginWithOneTimePassword,
  mobileLookup,
  newPersistentSession,
  revokePersistentSession,
  requestTokenFromApp,
  sendOneTimePassword,
  setSessionExpired,
  PersistentStepUpReason,
} from '../../'
import { Route } from '../../../config/router'
import {
  AFTERPAY_CHECKOUT_LOGIN_TOKEN,
  authenticate,
  Authentication,
  AuthenticationErrors,
  CASH_CHECKOUT_TOKEN,
  FingerPrint,
  isPublicResponse,
  MobileLogin,
  persistentSession,
  unauthenticate,
  VerificationMethod,
} from '../../../model'
import { PURGE } from 'redux-persist'

export interface AuthenticationState {
  status: Authentication
  error?: AuthenticationErrors
  profilingId: string | null
  fingerprint: FingerPrint | null
  enableOTP: boolean
  autoSendOTP: boolean
  enabled2faMethods: Record<VerificationMethod, boolean>
  initial2faMethod?: VerificationMethod
  autoSend2fa: boolean
  isTrustedCheckout: boolean
  mobileLogin: MobileLogin
  authToken?: string
  afterpayCheckoutLoginToken?: string
  cashCheckoutToken?: string
  isPrivateBrowsingMode: boolean
  mintInFlight: boolean
  recognizedViaPersistentLogin: boolean
  autoRememberMeStepUp: boolean
  autoRememberMeStepUpReason?: PersistentStepUpReason
  autoRememberMeStepUpAwaitingAccount: boolean
}

export const userInitialState: AuthenticationState = {
  status: unauthenticate(),
  profilingId: uuid(),
  fingerprint: null,
  enableOTP: true,
  autoSendOTP: true,
  enabled2faMethods: {
    SMS: true,
    EMAIL: false,
  },
  autoSend2fa: true,
  isTrustedCheckout: false,
  mobileLogin: {
    isMobileLogin: false,
    dateOfBirthVerificationRequired: false,
    // @ts-expect-error: OPERATION BLEED STOPPER
    verifiedRoute: null,
    hasSeenChallenge: false,
  },
  isPrivateBrowsingMode: false,
  mintInFlight: false,
  recognizedViaPersistentLogin: false,
  autoRememberMeStepUp: false,
  autoRememberMeStepUpAwaitingAccount: false,
}

const authErrors: Partial<Record<ErrorCode, AuthenticationErrors>> = {
  [ErrorCode.AccountLocked]: AuthenticationErrors.AccountLocked,
  [ErrorCode.AccountDisabled]: AuthenticationErrors.AccountDisabled,
  [ErrorCode.AuthenticationFailure]: AuthenticationErrors.IncorrectPassword,
  [ErrorCode.AuthenticationFailureMaxAttempts]: AuthenticationErrors.FailureMaxAttempts,
  [ErrorCode.TooManyOTPRequests]: AuthenticationErrors.TooManyOTPRequests,
  [ErrorCode.PaywatchCheckFailed]: AuthenticationErrors.PaywatchCheckFailed,
}

const slice: Slice = createSlice({
  name: 'auth',
  initialState: userInitialState,
  reducers: {
    setMobileLoginVerifiedRoute(state: AuthenticationState, action: PayloadAction<Route>) {
      state.mobileLogin.verifiedRoute = action.payload
    },
    setIsMobileLogin(state: AuthenticationState, action: PayloadAction<boolean>) {
      state.mobileLogin.isMobileLogin = action.payload
    },
    setAuthToken(state: AuthenticationState, action: PayloadAction<string>) {
      state.authToken = action.payload
    },
    setCashSessionAuthenticated(state: AuthenticationState, action: PayloadAction<string>) {
      state.authToken = action.payload
      state.status = authenticate(false, false)
    },
    setIsPrivateBrowsingMode(state: AuthenticationState, action: PayloadAction<boolean>) {
      state.isPrivateBrowsingMode = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestTokenFromApp.fulfilled, (state, action) => {
        if (action.payload.type === AFTERPAY_CHECKOUT_LOGIN_TOKEN) {
          state.afterpayCheckoutLoginToken = action.payload.payload
          state.cashCheckoutToken = undefined
        } else if (action.payload.type === CASH_CHECKOUT_TOKEN) {
          state.cashCheckoutToken = action.payload.payload
          state.afterpayCheckoutLoginToken = undefined
        }
      })
      .addCase(requestTokenFromApp.rejected, (state) => {
        state.afterpayCheckoutLoginToken = undefined
        state.cashCheckoutToken = undefined
      })
      .addCase(authLogin.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(authLogin.rejected, (state, action) => {
        state.status = unauthenticate()
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }
      })
      .addCase(authLogin.fulfilled, (state, action) => {
        const isPersistentSession = action.meta.arg['remember-me']
        state.enabled2faMethods.EMAIL = action.payload.user.shouldAllowEmail
        state.status = authenticate(action.payload.user.requires2fa, isPersistentSession, isPersistentSession)
      })
      .addCase(authLogout.fulfilled, (state) => {
        state.status = unauthenticate()
        state.authToken = undefined
        state.isTrustedCheckout = false
        state.mintInFlight = false
      })
      .addCase(checkPersistentLogin.fulfilled, (state, action) => {
        const { active, verify, allowEmail2Fa, isTrustedCheckout, stepUpReason } = action.payload
        state.status = persistentSession(active, verify)
        if (verify) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.enabled2faMethods.EMAIL = allowEmail2Fa
        }
        state.isTrustedCheckout = isTrustedCheckout
        state.recognizedViaPersistentLogin = active
        state.autoRememberMeStepUp = active && verify
        state.autoRememberMeStepUpReason = active && verify ? stepUpReason : undefined
        state.autoRememberMeStepUpAwaitingAccount = false
      })
      .addCase(check2faCode.fulfilled, (state) => {
        const rememberMeStepUpPassed = state.autoRememberMeStepUp && state.autoRememberMeStepUpReason !== undefined
        state.autoRememberMeStepUpAwaitingAccount = state.autoRememberMeStepUpAwaitingAccount || rememberMeStepUpPassed
        state.autoRememberMeStepUp = false
        state.autoRememberMeStepUpReason = undefined
      })
      .addCase(accountLookup.fulfilled, (state) => {
        if (state.autoRememberMeStepUpAwaitingAccount) {
          state.status.verificationRequired = false
          state.autoRememberMeStepUpAwaitingAccount = false
        }
      })
      .addCase(createFingerprint.fulfilled, (state, action) => {
        state.fingerprint = action.payload
      })
      .addCase(newPersistentSession.pending, (state) => {
        state.mintInFlight = true
      })
      .addCase(newPersistentSession.fulfilled, (state, action) => {
        if (state.mintInFlight) {
          state.status.persistent = action.payload.minted
          state.mintInFlight = false
        }
      })
      .addCase(newPersistentSession.rejected, (state) => {
        state.mintInFlight = false
      })
      .addCase(revokePersistentSession.pending, (state) => {
        if (state.status.authenticated) {
          state.status.persistent = false
        }
      })
      .addCase(emailLookup.pending, (state, action) => {
        state.mobileLogin = {
          isMobileLogin: action.meta.arg.isFromRegistration,
          // @ts-expect-error: OPERATION BLEED STOPPER
          mobile: null,
          dateOfBirthVerificationRequired: false,
          hasSeenChallenge: false,
        }
      })
      .addCase(emailLookup.fulfilled, (state, action) => {
        state.enabled2faMethods.SMS = action.payload.smsVerificationEnabled
        state.status.verificationRequired = action.payload.smsVerificationRequired
      })
      .addCase(mobileLookup.pending, (state) => {
        state.mobileLogin = {
          isMobileLogin: true,
          dateOfBirthVerificationRequired: false,
          // @ts-expect-error: OPERATION BLEED STOPPER
          verifiedRoute: null,
          hasSeenChallenge: false,
        }
      })
      .addCase(mobileLookup.fulfilled, (state, action) => {
        if (isPublicResponse(action.payload)) {
          return
        }
        const { passwordExists, dateOfBirthVerificationRequired, isFromInitialiseCheckout = false, existing } = action.payload
        const verifiedRoute = passwordExists ? null : Route.VERIFY
        state.mobileLogin = {
          isMobileLogin: isFromInitialiseCheckout ? existing : true,
          dateOfBirthVerificationRequired,
          // @ts-expect-error: OPERATION BLEED STOPPER
          verifiedRoute,
          hasSeenChallenge: false,
        }
        state.status.verificationRequired = action.payload.smsVerificationRequired
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        if (typeof action.payload.consumerLookup !== 'undefined') {
          const { cohort, action: lookupAction, recognizedBy } = action.payload.consumerLookup
          const verifiedRoute = lookupAction !== ConsumerLookupAction.SET_PASSWORD ? null : Route.VERIFY
          state.mobileLogin = {
            isMobileLogin: recognizedBy === ConsumerLookupRecognizedBy.MOBILE && cohort === ConsumerLookupCohort.existing_consumer,
            dateOfBirthVerificationRequired: lookupAction === ConsumerLookupAction.DATE_OF_BIRTH_VERIFICATION,
            // @ts-expect-error: OPERATION BLEED STOPPER
            verifiedRoute,
            hasSeenChallenge: false,
          }
          state.status.verificationRequired = lookupAction === ConsumerLookupAction.SMS_VERIFICATION
        }
      })
      .addCase(check2faDob.fulfilled, (state) => {
        state.mobileLogin.dateOfBirthVerificationRequired = false
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.mobileLogin.verifiedRoute = null
        state.mobileLogin.hasSeenChallenge = true
      })
      .addCase(sendOneTimePassword.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(sendOneTimePassword.rejected, (state, action) => {
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }

        if (state.error === AuthenticationErrors.TooManyOTPRequests) {
          state.enableOTP = false
        }
      })
      .addCase(sendOneTimePassword.fulfilled, (state) => {
        state.error = undefined
      })
      .addCase(loginWithOneTimePassword.pending, (state) => {
        state.error = undefined
        state.status = unauthenticate()
      })
      .addCase(loginWithOneTimePassword.rejected, (state, action) => {
        state.status = unauthenticate()
        if (action.meta.rejectedWithValue) {
          // @ts-expect-error: OPERATION BLEED STOPPER
          state.error = authErrors[action.payload.errorCode] ?? AuthenticationErrors.OtherError
        } else {
          state.error = AuthenticationErrors.OtherError
        }

        if (state.error === AuthenticationErrors.FailureMaxAttempts) {
          state.enableOTP = false
        }
      })
      .addCase(loginWithOneTimePassword.fulfilled, (state, action) => {
        state.status = authenticate(action.payload.user.requires2fa, false)
      })
      .addCase(confirmRegistrationMobile.fulfilled, (state) => {
        state.status.verificationRequired = false
      })
      .addCase(finaliseAccountDetails.fulfilled, (state) => {
        state.status = authenticate(false, false)
      })
      .addCase(closeCheckout, (state) => {
        state.authToken = undefined
        state.mintInFlight = false
      })
      .addCase(PURGE, () => userInitialState)
      .addMatcher(authSessionsService.endpoints.handoffAuthSession.matchFulfilled, (state, action) => {
        state.enabled2faMethods.EMAIL = action.payload.user.shouldAllowEmail
        state.status = authenticate(action.payload.user.requires2fa, false)
      })
      .addMatcher(
        isAnyOf(
          authLogin.pending,
          authLogin.rejected,
          authSessionsService.endpoints.handoffAuthSession.matchPending,
          sendOneTimePassword.pending,
          loginWithOneTimePassword.pending,
          loginWithOneTimePassword.rejected,
          authLogout.fulfilled,
          closeCheckout,
          setSessionExpired
        ),
        (state) => {
          state.recognizedViaPersistentLogin = false
          state.autoRememberMeStepUp = false
          state.autoRememberMeStepUpReason = undefined
          state.autoRememberMeStepUpAwaitingAccount = false
        }
      )
  },
})

export const { setAuthToken, setIsMobileLogin, setMobileLoginVerifiedRoute, setIsPrivateBrowsingMode, setCashSessionAuthenticated } = slice.actions

export default slice.reducer
